import { forwardRef } from 'react';

import { Checkbox } from '@any-ui-react/core';
import { tv } from 'tailwind-variants';

const selectChipVariants = tv({
  base: [
    // Layout & typography
    'text-2xs inline-flex items-center gap-2 rounded-md border px-4 py-1.5 font-normal transition-colors select-none',
    // Cursor
    'cursor-pointer data-[disabled]:cursor-not-allowed',
    // Default state
    'border-gray-3 text-gray-9 hover:border-gray-5 hover:bg-gray-1 bg-white',
    // Selected state
    'data-[selected]:border-blue-6 data-[selected]:bg-blue-6/10 data-[selected]:text-blue-9',
    // Disabled state
    'data-[disabled]:border-gray-3 data-[disabled]:bg-gray-1 data-[disabled]:text-gray-6 data-[disabled]:opacity-50',
    // Disabled + selected
    'data-[disabled]:data-[selected]:border-gray-4 data-[disabled]:data-[selected]:bg-gray-2',
  ],
});

export interface SelectChipProps {
  label: string;
  selected?: boolean;
  disabled?: boolean;
  showCheckbox?: boolean;
  onChange?: (selected: boolean) => void;
  className?: string;
}

export const SelectChip = forwardRef<HTMLDivElement, SelectChipProps>(
  (
    { label, selected = false, disabled = false, showCheckbox = true, onChange, className },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!selected);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
        e.preventDefault();
        onChange?.(!selected);
      }
    };

    return (
      <div
        ref={ref}
        role={showCheckbox ? 'checkbox' : 'option'}
        aria-checked={showCheckbox ? selected : undefined}
        aria-selected={!showCheckbox ? selected : undefined}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        data-selected={selected || undefined}
        data-disabled={disabled || undefined}
        className={selectChipVariants({ className })}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        {showCheckbox && (
          <Checkbox
            checked={selected}
            disabled={disabled}
            onChange={(e) => {
              e.stopPropagation();
              if (!disabled) {
                onChange?.(!selected);
              }
            }}
            tabIndex={-1}
            classNames={{
              input: disabled ? 'cursor-not-allowed' : 'cursor-pointer',
            }}
          />
        )}
        <span>{label}</span>
      </div>
    );
  }
);

SelectChip.displayName = 'SelectChip';
